import { ErrorKeys } from 'consts/errorKeys'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { giftAccessThunks } from 'store/features/aiAvatar/thunks/giftAccess'
import slice from 'store/features/aiAvatar/slice'
import { TAppDispatch, TGetState, TRootState } from 'store/types'
import { handleApiError } from 'store/utils'
import { generationProgressService } from 'services/generationProgress'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import {
  AVATAR_PROGRESS_CONFIG,
  buildRenderSecondsLookup,
  estimateAvatarRenderSecondsFromScreens
} from 'utils/avatar'
import { IAIAvatarSocketMessage } from './types'

// Re-estimate the render-phase duration from the REAL per-screen voiceover
// durations the backend reports at `voiceover_generated` (one part per screen),
// mapped through the model's per-(duration, quality) render catalog. Returns
// undefined when the parts are absent (older backend / dropped field) so
// `advancePhase` keeps the text-based estimate made at generation start.
const resolveRenderPhaseSeconds = (
  socketData: IAIAvatarSocketMessage,
  state: TRootState
): number | undefined => {
  const screenVideoSeconds = (socketData.voiceoverData ?? [])
    .map(part => part.duration)
    .filter(duration => duration > 0)
  if (!screenVideoSeconds.length) return undefined

  const renderSecondsByDuration =
    aiAvatarSelectors.avatarRenderSecondsByDuration(state)
  return estimateAvatarRenderSecondsFromScreens({
    screenVideoSeconds,
    renderSecondsForVideoDuration: buildRenderSecondsLookup(
      renderSecondsByDuration
    )
  })
}

/**
 * Completion handler: the avatar generation produced an array of video samples
 * (already full media items). Find the temporary `aiAvatar` placeholder and
 * replace it with those samples as permanent `aiAvatar` clips, laid out
 * consecutively on the placeholder's line — in the present timeline AND in every
 * undo/redo snapshot that still holds the placeholder, so the finished clips
 * survive undo/redo (e.g. generate -> undo -> generation finishes -> redo).
 */
const processCompletedAvatar =
  (socketData: IAIAvatarSocketMessage) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()

    const hash = socketData.ai_avatar_video_hash
    const projectId = String(socketData.ai_avatar_project_id)

    // Search present + past + future (`pendingAiAvatarMediaItems` folds in
    // `pendingMediasFromPastAndFuture`) so a completion that lands while the user
    // has undone past the placeholder still resolves to the right item instead
    // of being dropped. It may be gone entirely if the user deleted it before
    // the event arrived — then there is nothing to replace.
    //
    // Correlate the placeholder: fresh-gen carries the hash as the item `id`;
    // regen carries the generation's projectId as `assetId` (the clip's `id`
    // can't change). The `assetId === hash` branch is kept for legacy in-flight
    // regen clips created before regen switched to projectId-keying.
    const placeholder = projectDataSelectors
      .pendingAiAvatarMediaItems(state)
      .find(
        item =>
          item.id === hash ||
          item.assetId === hash ||
          item.assetId === projectId
      )
    if (!placeholder) return

    const samples = socketData.mediaItems
    if (!samples?.length) return

    // Splice the placeholder -> samples across present/past/future. This reducer
    // mutates the history stacks directly and never pushes to the undo stack, so
    // no `isUndoable` flag is needed (matches how aiVideo finalizes generation).
    dispatch(
      projectDataThunks.replacePendingPlaceholderWithSamples({
        id: placeholder.id,
        samples
      })
    )

    // Regeneration saves the original clip as a processing item (so a failure
    // can restore it). On success the clip has been replaced, so drop that
    // snapshot. No-op for fresh generations, which never save one.
    dispatch(projectDataThunks.removeFromProcessingItems(placeholder.id))

    // Snap the loader to 100% and tear down the generator + persisted
    // progress. The placeholder is already gone from the timeline, so this is
    // primarily cleanup (no zombie generator lingering in localStorage).
    generationProgressService.complete({
      mediaItemId: placeholder.id,
      assetId: placeholder.assetId
    })

    // A free user just consumed their free trial — re-check eligibility so the
    // generate footer flips to the upgrade state (self-guards to free users).
    // Also clear the mid-render awaiting flag: this is the fallback that releases
    // the footer loading state if the earlier `voiceover_generated` was dropped.
    dispatch(giftAccessThunks.checkGiftAccess())
    dispatch(slice.actions.setAwaitingGiftRecheck(false))
  }

/**
 * Failure handler: generation failed. Find the `aiAvatar` item we were
 * generating into and hand it to `handleFailedGenerationForMediaItem`, which
 * restores the clip's pre-generation data if a processing-item snapshot exists
 * (regeneration) or removes it otherwise (fresh generation), and tears down the
 * loader. If the item is already gone, just clean up the persisted progress.
 */
const processFailedAvatar =
  (socketData: IAIAvatarSocketMessage) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()

    const hash = socketData.ai_avatar_video_hash
    const projectId = String(socketData.ai_avatar_project_id)

    // Search present + past + future (mirrors `processCompletedAvatar`) so a
    // failure that lands while the user has undone past the placeholder still
    // resolves to the right clip — and restores its pre-generation data —
    // instead of being dropped, which would leave a regen snapshot dangling
    // and the clip stuck PENDING on redo.
    const placeholder = projectDataSelectors
      .pendingAiAvatarMediaItems(state)
      .find(
        item =>
          item.id === hash ||
          item.assetId === hash ||
          item.assetId === projectId
      )

    if (placeholder) {
      dispatch(
        projectDataThunks.handleFailedGenerationForMediaItem(placeholder.id)
      )

      // Surface the failure to the user (mirrors `processFailedAIVideo`): a
      // backend-supplied `message` is shown verbatim, otherwise the generic
      // avatar-generation-failed copy.
      dispatch(
        handleApiError(
          socketData.message
            ? ErrorKeys.CUSTOM_API_ERROR
            : ErrorKeys.AI_AVATAR_GENERATION_FAILED,
          socketData.message
        )
      )
    } else {
      // Item already deleted before the event arrived — just tear down the
      // loader and its persisted progress so it can't resume on reload.
      generationProgressService.stop({
        mediaItemId: hash,
        assetId: projectId,
        shouldRemoveFromStorage: true
      })
    }

    // Re-check eligibility and release the mid-render awaiting flag (fallback in
    // case `voiceover_generated` never arrived) — mirrors processCompletedAvatar.
    dispatch(giftAccessThunks.checkGiftAccess())
    dispatch(slice.actions.setAwaitingGiftRecheck(false))
  }

/**
 * Mid-render milestone: the voiceover (TTS) has been generated, which is when a
 * free user's gift/credit is actually consumed — and the point where the backend
 * hands off from prep to rendering the screens. Two effects:
 *
 * 1. Progress: advance the loader from the prep phase (capped at
 *    VOICEOVER_PHASE_PERCENT) into the render phase (→99), re-anchored to the
 *    render duration RE-ESTIMATED from the real per-screen voiceover durations in
 *    the payload (falling back to the start-time estimate when absent). Correlate
 *    by projectId — fresh-gen keys the generator's assetId to it, regen re-keys
 *    the clip's assetId to it — so `advancePhase` resolves the right generator.
 *    No-op if the generator is gone (already finished, or never phased).
 * 2. Gift: re-check gift access so the generate footer flips to the correct
 *    state (free-trial again if credits remain, or upgrade), then release the
 *    footer loading state. `checkGiftAccess` self-guards to free users, and
 *    `isAwaitingGiftRecheck` is only ever set for free users, so it's a no-op for
 *    paid users. Gift access is per-user, so no clip correlation is needed there.
 */
const processVoiceoverGenerated =
  (socketData: IAIAvatarSocketMessage) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    generationProgressService.advancePhase({
      assetId: String(socketData.ai_avatar_project_id),
      phaseFloor: AVATAR_PROGRESS_CONFIG.VOICEOVER_PHASE_PERCENT,
      phaseCap: AVATAR_PROGRESS_CONFIG.RENDER_PHASE_CAP_PERCENT,
      durationSeconds: resolveRenderPhaseSeconds(socketData, getState())
    })

    await dispatch(giftAccessThunks.checkGiftAccess())
    dispatch(slice.actions.setAwaitingGiftRecheck(false))
  }

export const aiAvatarSocketHandlers = {
  processCompletedAvatar,
  processFailedAvatar,
  processVoiceoverGenerated
}
